import React, { useState } from "react";
import { Button } from "@penmate/react-shared/ui/button";
import { US_STATES } from "@penmate/react-shared/lib/usStates";
import { cn } from "@penmate/react-shared/lib/utils";
import { Label, Select, TextInput } from "./fields";
import type { SearchDraft, SearchMode } from "./types";
import { useKeyboardLayout } from "./keyboardLayout";
import { typedName } from "./names";
import heroIllustration from "../assets/hero-illus-main.png";

// The illustration's dashed orbit, under a word or a name: a hand-drawn teal
// stroke painted under each line the text takes (a long name may wrap), drawn
// in after the headline has landed. Decorative — the text stands alone.
const Orbit = ({ children }: { children: React.ReactNode }) => <span className="pm-orbit-word">{children}</span>;

const MODES: Array<{ id: SearchMode; label: string }> = [
  { id: "state", label: "State or County" },
  { id: "federal", label: "Federal" },
];

export const WhoStep = ({
  initial,
  greetingName,
  facilityName,
  facilityState,
  onCommit,
}: {
  initial?: Partial<SearchDraft>;
  // Inbound arrivals are greeted by name; the form is already theirs.
  greetingName?: string;
  // A facility that arrived with the link — the where step starts from it.
  // Shown only while the form still points at that facility's state.
  facilityName?: string;
  facilityState?: string;
  onCommit: (draft: SearchDraft) => void;
}) => {
  const [mode, setMode] = useState<SearchMode>(initial?.mode || "state");
  const [firstName, setFirstName] = useState(initial?.firstName || "");
  const [lastName, setLastName] = useState(initial?.lastName || "");
  const [state, setState] = useState(initial?.state || "");
  const [inmateId, setInmateId] = useState(initial?.inmateId || "");
  const [birthdate, setBirthdate] = useState(initial?.birthdate || "");
  const [showDetail, setShowDetail] = useState(!!initial?.birthdate);
  // The headline follows the form, not the URL: it takes the arrival name and
  // re-commits whenever a name field blurs, so a fixed typo fixes the greeting.
  const [committedName, setCommittedName] = useState<string | null>(greetingName || null);
  const commitName = () => {
    const name = `${firstName.trim()} ${lastName.trim()}`.trim();
    setCommittedName(firstName.trim() && lastName.trim() ? name : null);
  };

  // With the keyboard up for a text field, the sticky CTA would only cover
  // the form: it steps aside until the field blurs (the form's padding then
  // clears the keyboard instead of the bar — see the keyboard styles).
  const keyboard = useKeyboardLayout();
  const ctaAside = keyboard.open && keyboard.typing;

  const ready =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    (mode === "federal" || state.length > 0);

  const commit = () => {
    if (!ready) return;
    // Tidied once here, so every headline and the contact agree on the name.
    const draft: SearchDraft = {
      mode,
      firstName: typedName(firstName),
      lastName: typedName(lastName),
    };
    if (mode === "state") {
      draft.state = state;
      if (inmateId.trim()) draft.inmateId = inmateId.trim();
      if (birthdate) draft.birthdate = birthdate;
    } else if (inmateId.trim()) {
      draft.inmateId = inmateId.trim();
    }
    onCommit(draft);
  };

  return (
    <div className="flex min-h-full w-full flex-col" aria-label="Who are you looking for">
      {/* The hero zone sits on the brand-purple field; the form rides a white
          sheet. The illustration scales with the viewport so the sheet keeps
          its room on short screens and the hero keeps its presence on tall ones. */}
      {/* The sheet overlaps the illustration's base so she stands behind the
          card — depth, and a little more room for the form. */}
      <div className="pm-kb-hide flex justify-center pt-1">
        <img
          src={heroIllustration}
          alt=""
          className="h-[clamp(6.5rem,21vh,11rem)] w-auto"
          aria-hidden="true"
        />
      </div>

      <div className="pm-kb-sheet relative z-10 -mt-7 flex flex-1 flex-col rounded-t-[28px] bg-background">
        {/* Bottom padding clears the sticky CTA bar so the last controls can
            scroll fully above it. */}
        <div className="pm-kb-form flex-1 px-6 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-5">
          {/* First touch: the headline wears the field's purple, ends on a beat
              of brand colour, and the word that matters — or the name — carries
              the illustration's own dashed orbit line, drawn in a beat later. */}
          <h1 className="pm-first-in text-[30px] font-extrabold leading-[1.2] tracking-[-0.025em] text-[hsl(var(--brand-purple))]">
            {committedName ? (
              <>
                Connect with <Orbit>{committedName}</Orbit>
              </>
            ) : (
              <>
                Find and write an <Orbit>inmate</Orbit>
              </>
            )}
            <span className="text-primary" aria-hidden="true">
              .
            </span>
          </h1>
          <p className="pm-first-in mt-2.5 text-base leading-relaxed text-muted-foreground [animation-delay:60ms]">
            Search by name and location, then send messages and photos in minutes.
            {facilityName &&
            mode === "state" &&
            facilityState &&
            state.toUpperCase() === facilityState.toUpperCase()
              ? ` We'll start with ${facilityName}.`
              : null}
          </p>

          <div
            role="group"
            aria-label="Where are they held?"
            className="pm-first-in mt-4 grid grid-cols-2 gap-1 rounded-2xl bg-secondary p-1 [animation-delay:120ms]"
          >
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                aria-pressed={mode === m.id}
                className={cn(
                  "h-11 rounded-xl text-[15px] font-semibold transition-colors",
                  mode === m.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground",
                )}
                onClick={() => {
                  if (m.id === mode) return;
                  setMode(m.id);
                  // A register number and a state ID are different identifiers —
                  // don't let one silently ride a search in the other mode.
                  setInmateId("");
                  setBirthdate("");
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className="pm-first-in mt-4 flex flex-col gap-3.5 [animation-delay:180ms]">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pm-search-first">Their first name</Label>
                <TextInput
                  id="pm-search-first"
                  type="search"
                  enterKeyHint="next"
                  autoComplete="off"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={commitName}
                />
              </div>
              <div>
                <Label htmlFor="pm-search-last">Their last name</Label>
                <TextInput
                  id="pm-search-last"
                  type="search"
                  enterKeyHint="done"
                  autoComplete="off"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={commitName}
                />
              </div>
            </div>

            {mode === "state" ? (
              <>
                <div>
                  <Label htmlFor="pm-search-state">State</Label>
                  <Select id="pm-search-state" value={state} onChange={(e) => setState(e.target.value)}>
                    <option value="" disabled>
                      Choose a state
                    </option>
                    {US_STATES.map((s) => (
                      <option key={s.abbrev} value={s.abbrev}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="pm-search-id">ID number (optional)</Label>
                  <TextInput
                    id="pm-search-id"
                    type="search"
                    enterKeyHint="done"
                    autoComplete="off"
                    placeholder="If you know it"
                    value={inmateId}
                    onChange={(e) => setInmateId(e.target.value)}
                  />
                </div>
                {showDetail ? (
                  <div>
                    <Label htmlFor="pm-search-dob">Date of birth</Label>
                    <TextInput
                      id="pm-search-dob"
                      type="date"
                      min="1900-01-01"
                      max={new Date().toISOString().slice(0, 10)}
                      value={birthdate}
                      onChange={(e) => setBirthdate(e.target.value)}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="min-h-[44px] self-start text-[15px] font-semibold text-primary"
                    onClick={() => setShowDetail(true)}
                  >
                    Know their birthday? (optional)
                  </button>
                )}
              </>
            ) : (
              <div>
                <Label htmlFor="pm-search-register">Register number (optional)</Label>
                <TextInput
                  id="pm-search-register"
                  type="search"
                  enterKeyHint="done"
                  autoComplete="off"
                  placeholder="If you know it"
                  value={inmateId}
                  onChange={(e) => setInmateId(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        {!ctaAside && (
          <div className="sticky bottom-0 bg-background/95 px-6 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-sm">
            <Button
              size="lg"
              className="h-[52px] w-full rounded-xl text-[17px] font-semibold"
              disabled={!ready}
              onClick={commit}
            >
              Find them
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
